import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

// DEV MODE: Set to false to require login (true skips login for development)
const DEV_SKIP_LOGIN = false;

// Mock user for development
const DEV_USER = {
  _id: 'dev-user-123',
  name: 'Developer',
  email: 'dev@geoventory.com',
  role: 'administrator',
  activeRole: 'administrator',
  preferences: {
    defaultCommodity: null,
    defaultRegion: null
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize from localStorage or DEV_USER
  const savedToken = localStorage.getItem('token');
  const [user, setUser] = useState(() => {
    if (DEV_SKIP_LOGIN) {
      // In dev mode, store dev-token in localStorage for API calls
      localStorage.setItem('token', 'dev-token');
      return DEV_USER;
    }
    return null;
  });
  const [loading, setLoading] = useState(DEV_SKIP_LOGIN ? false : !savedToken ? false : true);
  const [token, setToken] = useState(DEV_SKIP_LOGIN ? 'dev-token' : savedToken);

  useEffect(() => {
    // Skip auth check in dev mode
    if (DEV_SKIP_LOGIN) {
      localStorage.setItem('token', 'dev-token');
      return;
    }
    
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const register = async (name, email, password, role) => {
    try {
      const response = await api.post('/auth/register', {
        name,
        email,
        password,
        role
      });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const switchRole = async (role) => {
    // In dev mode, just update local state
    if (DEV_SKIP_LOGIN) {
      setUser(prev => ({
        ...prev,
        role: role,
        activeRole: role
      }));
      return { success: true };
    }
    
    try {
      const response = await api.put('/auth/switch-role', { role });
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to switch role'
      };
    }
  };

  const updatePreferences = async (preferences) => {
    try {
      const response = await api.put('/auth/preferences', preferences);
      setUser(prev => ({
        ...prev,
        preferences: response.data.preferences
      }));
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update preferences'
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    switchRole,
    updatePreferences
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
